import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';

const InviteFriends = () => {
  const { currentUser, userDetails } = useAuth();
  
  const [emails, setEmails] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [inviteType, setInviteType] = useState<'participate' | 'donate'>('participate');
  const [donationAmount, setDonationAmount] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  
  // Default message templates
  const defaultParticipateMessage = `Hey!
I wanted to share something cool I'm doing this month—A Million Miles May.
The goal is to walk one million miles collectively across Canada during May 2025.
I'd love for you to join me!
It's a fun way to stay active and be part of something bigger.
Any donation—big or small—makes a difference and is truly appreciated.
And hey, if you think your co-workers, friends, or even your customers would be into this, feel free to spread the word or get them involved too!

Here's the link to sign up:
https://millionmilesmay.web.app/register
Hope to see you on the leaderboard!

${userDetails?.firstName || 'Your friend'}`;

  const defaultDonateMessage = `Hi there,

I'm participating in the Million Miles May challenge, walking to contribute to our goal of one million miles during May 2025.

I'd appreciate your support through a donation. Your contribution will support 20 impactful charities through HealthPartners, promoting wellness and vital programs across Canada.

You can donate here: https://millionmilesmay.web.app/donate

Thank you for your generosity!
${userDetails?.firstName || 'Your friend'}`;

  // Set default message based on invite type
  React.useEffect(() => {
    if (inviteType === 'participate') {
      setMessage(defaultParticipateMessage);
    } else {
      setMessage(defaultDonateMessage);
    }
  }, [inviteType, userDetails, defaultParticipateMessage, defaultDonateMessage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!emails.trim()) {
      setError('Please enter at least one email address');
      return;
    }
    
    const emailList = emails.split(',').map(email => email.trim());
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    const invalidEmails = emailList.filter(email => !emailRegex.test(email));
    if (invalidEmails.length > 0) {
      setError(`Invalid email address(es): ${invalidEmails.join(', ')}`);
      return;
    }
    
    if (inviteType === 'donate' && donationAmount && isNaN(Number(donationAmount))) {
      setError('Please enter a valid donation amount');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Call the sendInvitation Cloud Function
      const sendInvitationFn = httpsCallable(functions, 'sendInvitation');
      
      // Prepare subject line
      let subject = '';
      const senderName = userDetails?.firstName || 'A friend';
      
      if (inviteType === 'participate') {
        subject = `${senderName} invites you to join the Million Miles May challenge!`;
      } else {
        subject = donationAmount 
          ? `${senderName} asks you to support their Million Miles May challenge with a $${donationAmount} donation`
          : `${senderName} asks you to support their Million Miles May challenge with a donation`;
      }
      
      console.log('Preparing to send invitation emails to:', emailList);
      
      // Call the Cloud Function
      const result = await sendInvitationFn({
        emails: emailList,
        subject,
        message,
        invitationType: inviteType,
        suggestedAmount: donationAmount || null
      });
      
      console.log('Cloud Function response:', result.data);
      
      // Check if all emails were sent successfully
      const data = result.data as any;
      if (data && data.success) {
        setSuccess(true);
        setEmails('');
        if (inviteType === 'donate') {
          setDonationAmount('');
        }
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setSuccess(false);
        }, 5000);
      } else {
        // Extract more detailed error information
        let errorMessage = 'Failed to send some or all invitations.';
        
        if (data && data.summary) {
          const { total, successful, failed } = data.summary;
          if (failed === total) {
            errorMessage = 'Failed to send all invitations. Please try again.';
          } else if (failed > 0) {
            errorMessage = `Sent ${successful} of ${total} invitations. ${failed} failed to send.`;
          }
        }
        
        // If we have specific error details from a failed email, show that too
        const errorDetail = data && data.results && data.results.find((r: any) => !r.success);
        if (errorDetail) {
          errorMessage += ` Error: ${errorDetail.error}`;
        }
        
        setError(errorMessage);
      }
      
    } catch (error: any) {
      console.error('Error sending invitations:', error);
      const errorMessage = error.message || 'Failed to send invitations. Please try again.';
      setError(errorMessage);
      
      if (error.code === 'functions/internal') {
        console.error('Internal function error details:', error.details);
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-primary py-4 px-6">
            <h2 className="text-xl font-semibold text-white">Invite Friends & Family</h2>
          </div>
          
          <div className="p-6">
            {success && (
              <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4">
                <p className="text-green-700">Your invitations have been sent successfully!</p>
              </div>
            )}
            
            {error && (
              <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4">
                <p className="text-red-700">{error}</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Invitation Type</label>
                <div className="mt-2 space-y-4">
                  <div className="flex items-center">
                    <input
                      id="invite-participate"
                      name="inviteType"
                      type="radio"
                      value="participate"
                      checked={inviteType === 'participate'}
                      onChange={() => setInviteType('participate')}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    />
                    <label htmlFor="invite-participate" className="ml-3 block text-sm font-medium text-gray-700">
                      Invite to participate in the challenge
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="invite-donate"
                      name="inviteType"
                      type="radio"
                      value="donate"
                      checked={inviteType === 'donate'}
                      onChange={() => setInviteType('donate')}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    />
                    <label htmlFor="invite-donate" className="ml-3 block text-sm font-medium text-gray-700">
                      Ask for a donation or sponsorship
                    </label>
                  </div>
                </div>
              </div>
              
              {inviteType === 'donate' && (
                <div>
                  <label htmlFor="donationAmount" className="block text-sm font-medium text-gray-700">
                    Suggested Donation Amount (optional)
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="text"
                      name="donationAmount"
                      id="donationAmount"
                      className="focus:ring-primary focus:border-primary block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                      placeholder="0.00"
                      value={donationAmount}
                      onChange={(e) => setDonationAmount(e.target.value)}
                    />
                  </div>
                </div>
              )}
              
              <div>
                <label htmlFor="emails" className="block text-sm font-medium text-gray-700">
                  Email Addresses
                </label>
                <div className="mt-1">
                  <textarea
                    id="emails"
                    name="emails"
                    rows={3}
                    className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter email addresses separated by commas"
                    value={emails}
                    onChange={(e) => setEmails(e.target.value)}
                    required
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Enter multiple email addresses separated by commas
                </p>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                  Message
                </label>
                <div className="mt-1">
                  <textarea
                    id="message"
                    name="message"
                    rows={8}
                    className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Personalize your message or use the default template
                </p>
              </div>
              
              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-secondary hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                >
                  {loading ? 'Sending...' : 'Send Invitations'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteFriends; 